/**
 * /logboek — het globale wijzigingslogboek (actor-attributie fase 3).
 *
 * Eén tijdlijn over alle schrijfpaden heen: wie deed wat, via welk kanaal.
 * Dit is het toezichtsvenster op de autonomie: naarmate het Brein meer zelf
 * doet, is hier zichtbaar wát het deed. Rijen zijn domme regels
 * (samenvatting-eerst, action-label als vangnet); doorclicken gaat via het
 * ordernummer naar de orderpagina.
 *
 * Rollen: admin + medewerker (zelfde vlak als de orderpagina's).
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { fetchLogboek, type LogboekEntry } from '../lib/logboek';
import { ACTION_LABELS, CHANNEL_BADGES, KANALEN, actorLabel } from '../lib/audit-labels';

const STATUSSEN = ['success', 'geweigerd', 'error', 'pending', 'schaduw'] as const;

const DAGEN_OPTIES = [
  { waarde: 1, label: 'Vandaag' },
  { waarde: 7, label: '7 dagen' },
  { waarde: 30, label: '30 dagen' },
  { waarde: 90, label: '90 dagen' },
];

const PAGE_SIZE = 100;

function tijdstip(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('nl-NL', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function LogboekPage() {
  const [entries, setEntries] = useState<LogboekEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [channel, setChannel] = useState('');
  const [status, setStatus] = useState('');
  const [dagen, setDagen] = useState(7);
  const [zoekActor, setZoekActor] = useState('');
  const [zoekOrder, setZoekOrder] = useState('');
  const [offset, setOffset] = useState(0);

  const laad = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchLogboek({
        channel: channel || undefined,
        status: status || undefined,
        dagen,
        actor: zoekActor || undefined,
        order: zoekOrder || undefined,
        limit: PAGE_SIZE,
        offset,
      });
      setEntries(result.entries);
      setTotal(result.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Logboek laden mislukt');
    } finally {
      setLoading(false);
    }
  }, [channel, status, dagen, zoekActor, zoekOrder, offset]);

  useEffect(() => {
    // Zoekvelden licht debouncen zodat niet elke toetsaanslag een query kost.
    const t = setTimeout(laad, zoekActor || zoekOrder ? 300 : 0);
    return () => clearTimeout(t);
  }, [laad, zoekActor, zoekOrder]);

  const setFilter = <T,>(setter: (v: T) => void) => (v: T) => {
    setOffset(0);
    setter(v);
  };

  return (
    <div className="h-full overflow-y-auto p-6 max-w-[1400px] mx-auto">
      <div className="flex items-baseline gap-3 mb-1">
        <h1 className="text-2xl font-heading font-semibold text-main">Logboek</h1>
        {!loading && <span className="text-sm text-slate-500">{total} wijzigingen</span>}
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Elke wijziging in het platform, met wie het deed en via welk kanaal: kantoor, klant,
        voice-agent, field-app of het Brein zelf.
      </p>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex flex-wrap gap-1">
          <button
            type="button"
            onClick={() => setFilter(setChannel)('')}
            className={clsx(
              'text-xs px-2 py-1 rounded-full border',
              channel === ''
                ? 'bg-slate-800 text-white border-slate-800'
                : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'
            )}
          >
            Alle kanalen
          </button>
          {KANALEN.map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setFilter(setChannel)(channel === k ? '' : k)}
              className={clsx(
                'text-xs px-2 py-1 rounded-full border',
                channel === k
                  ? 'bg-slate-800 text-white border-slate-800'
                  : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'
              )}
            >
              {k}
            </button>
          ))}
        </div>

        <select
          value={status}
          onChange={(e) => setFilter(setStatus)(e.target.value)}
          className="text-xs border border-slate-300 rounded px-2 py-1 bg-white text-slate-700"
        >
          <option value="">Alle statussen</option>
          {STATUSSEN.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>

        <select
          value={dagen}
          onChange={(e) => setFilter(setDagen)(Number(e.target.value))}
          className="text-xs border border-slate-300 rounded px-2 py-1 bg-white text-slate-700"
        >
          {DAGEN_OPTIES.map((o) => (
            <option key={o.waarde} value={o.waarde}>{o.label}</option>
          ))}
        </select>

        <input
          type="text"
          value={zoekActor}
          onChange={(e) => setFilter(setZoekActor)(e.target.value)}
          placeholder="Actor…"
          className="text-xs border border-slate-300 rounded px-2 py-1 w-32"
        />
        <input
          type="text"
          value={zoekOrder}
          onChange={(e) => setFilter(setZoekOrder)(e.target.value)}
          placeholder="Ordernummer…"
          className="text-xs border border-slate-300 rounded px-2 py-1 w-32"
        />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 mb-4">
          {error}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 divide-y divide-slate-100">
        {loading && entries.length === 0 ? (
          <p className="p-4 text-sm text-slate-400">Laden…</p>
        ) : entries.length === 0 ? (
          <p className="p-4 text-sm text-slate-400">Geen wijzigingen binnen deze filters.</p>
        ) : (
          entries.map((entry) => {
            const badge = CHANNEL_BADGES[entry.channel];
            return (
              <div
                key={entry.id}
                className={clsx(
                  'flex items-start gap-3 px-4 py-2 text-sm',
                  entry.status === 'geweigerd' && 'opacity-60',
                  entry.status === 'error' && 'bg-amber-50/50'
                )}
              >
                <span className="text-xs text-slate-400 whitespace-nowrap w-28 pt-0.5">
                  {tijdstip(entry.createdAt)}
                </span>
                <div className="flex-1 min-w-0">
                  <span className="font-medium text-slate-900">{actorLabel(entry)}</span>
                  <span className="text-slate-600">
                    {' '}— {entry.samenvatting ?? ACTION_LABELS[entry.action] ?? entry.action}
                  </span>
                  {badge && (
                    <span className={clsx('ml-1.5 text-[10px] px-1.5 py-0.5 rounded align-middle', badge.className)}>
                      {badge.label}
                    </span>
                  )}
                  {entry.status === 'geweigerd' && (
                    <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 align-middle">
                      geweigerd{entry.errorCode ? `: ${entry.errorCode}` : ''}
                    </span>
                  )}
                  {entry.status === 'error' && (
                    <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 align-middle">
                      fout{entry.errorCode ? `: ${entry.errorCode}` : ''}
                    </span>
                  )}
                  {entry.status === 'error' && entry.errorMessage && (
                    <p className="text-xs text-amber-700 mt-0.5 truncate">{entry.errorMessage}</p>
                  )}
                </div>
                {entry.orderId && (
                  <Link
                    to={`/orders/${entry.orderId}`}
                    className="text-xs text-indigo-600 hover:underline whitespace-nowrap pt-0.5"
                  >
                    {entry.orderNumber ?? 'order'}
                  </Link>
                )}
              </div>
            );
          })
        )}
      </div>

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between mt-3 text-xs text-slate-500">
          <button
            type="button"
            disabled={offset === 0 || loading}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            className="px-2 py-1 rounded border border-slate-300 bg-white disabled:opacity-40 hover:border-slate-400"
          >
            Nieuwer
          </button>
          <span>
            {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} van {total}
          </span>
          <button
            type="button"
            disabled={offset + PAGE_SIZE >= total || loading}
            onClick={() => setOffset(offset + PAGE_SIZE)}
            className="px-2 py-1 rounded border border-slate-300 bg-white disabled:opacity-40 hover:border-slate-400"
          >
            Ouder
          </button>
        </div>
      )}
    </div>
  );
}
